import type { Direction } from "../core/Input";
import type { TileDef, TileSet } from "./Tiles";
import type { CharacterSpec } from "../render/CharacterModel";
import type { MonsterAppearance } from "../render/MonsterModel";

export interface MonsterSlot {
  id: string;
  /** How many appear; a single number or an inclusive [min, max]. */
  count?: number | [number, number];
}

export interface EncounterGroup {
  monsters: MonsterSlot[];
  weight?: number;
}

export interface EncounterTable {
  /**
   * `symbols` puts monsters on the map to be seen and avoided (or run into);
   * `random` is the older invisible-step-counter behaviour. Defaults to
   * symbols.
   */
  mode?: "symbols" | "random";
  groups: EncounterGroup[];

  // --- symbol encounters ---
  /** How many roam the map at once. */
  maxActive?: number;
  /** Seconds before a beaten monster is replaced. */
  respawn?: number;
  /** Distance, in world units, at which a monster notices the party. */
  aggro?: number;

  // --- random encounters ---
  /** Chance per tile walked, before the tile's `encounterScale`. */
  rate?: number;

  /** Distance of guaranteed safety after a battle or a map change. */
  grace?: number;
}

export interface ExitDef {
  x: number;
  y: number;
  to: string;
  toX: number;
  toY: number;
  facing?: Direction;
  /** Requires pressing confirm rather than triggering on step. */
  manual?: boolean;
}

export interface NpcDef {
  id?: string;
  x: number;
  y: number;
  facing?: Direction;
  name?: string;
  /** Omit for invisible interactables such as signs. */
  appearance?: CharacterSpec;
  /** Renders a creature instead of a person — bosses standing on the map. */
  monster?: MonsterAppearance;
  /** Dialogue pages shown when talked to. */
  lines?: string[];
  movement?: "fixed" | "wander" | "spin";
  /** Turns to face the player when spoken to. Defaults to true. */
  turnsToPlayer?: boolean;
  /** Arbitrary payload for game-specific interaction handlers. */
  tag?: string;
  /** Only placed when the party has this flag. */
  requiresFlag?: string;
  /** Removed once the party has this flag. */
  hiddenByFlag?: string;
}

export interface ChestDef {
  x: number;
  y: number;
  /** Item id granted on opening. */
  item?: string;
  gold?: number;
  /** Party flag that records the chest as looted. Defaults to `chest:map:x:y`. */
  flag?: string;
}

export interface MapDef {
  id: string;
  name: string;
  /** Single character -> tile id. */
  legend: Record<string, string>;
  /** ASCII rows, top row is y = 0. All rows must be the same length. */
  rows: string[];
  spawn?: { x: number; y: number; facing?: Direction };
  encounter?: EncounterTable;
  npcs?: NpcDef[];
  chests?: ChestDef[];
  exits?: ExitDef[];
  /** Background/fog colour. */
  sky?: number;
  fog?: { near: number; far: number };
  ambient?: number;
  /**
   * Sky colour used for ambient light. Defaults to `sky`, which is wrong
   * underground: a black sky means a black hemisphere light and a cave lit by
   * nothing at all.
   */
  ambientColor?: number;
  /** Sunlight tint. Caves want something cold and dim. */
  sunColor?: number;
  sunIntensity?: number;
  /** Bounce light from the opposite side; keeps backlit faces from going black. */
  fillColor?: number;
  fillIntensity?: number;
  music?: string;
  /** Shown as a banner when the map is entered. */
  title?: string;
}

/** Parsed, queryable map. Owns no rendering state. */
export class TileMap {
  readonly def: MapDef;
  readonly width: number;
  readonly height: number;
  private readonly grid: string[][];

  constructor(def: MapDef, private tileset: TileSet) {
    this.def = def;
    this.height = def.rows.length;
    this.width = def.rows[0]?.length ?? 0;

    this.grid = def.rows.map((row, y) => {
      if (row.length !== this.width) {
        throw new Error(`Map "${def.id}" row ${y} is ${row.length} wide, expected ${this.width}`);
      }
      return [...row].map((ch, x) => {
        const id = def.legend[ch];
        if (!id) throw new Error(`Map "${def.id}" has no legend entry for '${ch}' at ${x},${y}`);
        if (!tileset.has(id)) throw new Error(`Map "${def.id}" references unknown tile "${id}"`);
        return id;
      });
    });
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  tileIdAt(x: number, y: number): string | null {
    return this.inBounds(x, y) ? this.grid[y][x] : null;
  }

  at(x: number, y: number): TileDef | null {
    const id = this.tileIdAt(x, y);
    return id ? this.tileset.get(id) : null;
  }

  /** Out-of-bounds counts as solid so the player cannot walk off the edge. */
  isSolid(x: number, y: number): boolean {
    const tile = this.at(x, y);
    return tile === null || tile.solid === true;
  }

  encounterScaleAt(x: number, y: number): number {
    return this.at(x, y)?.encounterScale ?? 1;
  }

  exitAt(x: number, y: number): ExitDef | undefined {
    return this.def.exits?.find((e) => e.x === x && e.y === y);
  }

  /** Iterates every cell with its tile definition. */
  forEach(fn: (x: number, y: number, tile: TileDef) => void): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        fn(x, y, this.tileset.get(this.grid[y][x]));
      }
    }
  }
}

export const DIRECTION_VECTORS: Record<Direction, { x: number; y: number }> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/**
 * Y rotation for a model facing each direction. Models are authored facing +Z,
 * which is "down" the map (towards the camera).
 */
export const DIRECTION_ANGLES: Record<Direction, number> = {
  down: 0,
  right: Math.PI / 2,
  up: Math.PI,
  left: -Math.PI / 2,
};

export function oppositeDirection(dir: Direction): Direction {
  return dir === "up" ? "down" : dir === "down" ? "up" : dir === "left" ? "right" : "left";
}
